import { getAppObj } from './utils';
import { saveSsoTokens } from './tokens';

const REFRESH_WINDOW = Object.freeze({
  seconds: 60,
  scheduleSkewSeconds: 5
});

const MIN_REFRESH_DELAY_MS = 5000;
const REFRESH_RETRY_DELAY_MS = 15000;
const DEFAULT_TOKEN_TTL_SECONDS = 300;

export const FORCE_REFRESH_MIN_VALIDITY = -1;

export const REFRESH_OUTCOME = Object.freeze({
  DONE: 'done',
  NOT_DUE: 'not-due',
  UNREACHABLE: 'unreachable',
  DEAD: 'dead'
});

let refreshTimeoutId = null;
let tokenObtainedAt = null;
let tokenExpiresIn = null;
let refreshTokenExpiresIn = null;
let sessionEstablished = false;
let sessionDeadHandled = false;
let sessionDeadHandler = null;
let visibilityListenerAttached = false;

function clearScheduledRefresh() {
  if (refreshTimeoutId !== null) {
    clearTimeout(refreshTimeoutId);
    refreshTimeoutId = null;
  }
}

function captureTokenTiming(keycloak) {
  tokenObtainedAt = Date.now();
  tokenExpiresIn =
    keycloak.tokenParsed?.exp && keycloak.tokenParsed?.iat
      ? keycloak.tokenParsed.exp - keycloak.tokenParsed.iat
      : null;
  refreshTokenExpiresIn =
    keycloak.refreshTokenParsed?.exp && keycloak.refreshTokenParsed?.iat
      ? keycloak.refreshTokenParsed.exp - keycloak.refreshTokenParsed.iat
      : null;
  sessionEstablished = true;
}

function getRemainingTokenSeconds() {
  if (tokenObtainedAt === null || tokenExpiresIn === null) return null;

  return tokenExpiresIn - (Date.now() - tokenObtainedAt) / 1000;
}

export function isKeycloakSessionDead() {
  if (!sessionEstablished) return false;

  if (
    tokenObtainedAt !== null &&
    refreshTokenExpiresIn !== null &&
    (Date.now() - tokenObtainedAt) / 1000 >= refreshTokenExpiresIn
  ) {
    return true;
  }

  const keycloak = getAppObj().keycloak;

  return !keycloak?.authenticated || !keycloak.refreshToken;
}

function isTokenEndpointClientError(error) {
  const status = error?.response?.status;

  return Number.isInteger(status) && status >= 400 && status < 500;
}

function handleDeadSession() {
  clearScheduledRefresh();

  if (!sessionDeadHandled && sessionDeadHandler) {
    sessionDeadHandled = true;
    sessionDeadHandler();
  }
}

export function setKeycloakSessionDeadHandler(handler) {
  if (typeof handler !== 'function') {
    throw new TypeError('Keycloak session-dead handler must be a function');
  }

  sessionDeadHandler = handler;
}

function scheduleRetry() {
  clearScheduledRefresh();
  refreshTimeoutId = setTimeout(runScheduledRefresh, REFRESH_RETRY_DELAY_MS);
}

function scheduleRefresh() {
  clearScheduledRefresh();

  const remaining = getRemainingTokenSeconds();
  const secondsLeft =
    remaining === null ? tokenExpiresIn || DEFAULT_TOKEN_TTL_SECONDS : remaining;
  const secondsUntilRefresh =
    secondsLeft - (REFRESH_WINDOW.seconds - REFRESH_WINDOW.scheduleSkewSeconds);

  refreshTimeoutId = setTimeout(
    runScheduledRefresh,
    Math.max(secondsUntilRefresh * 1000, MIN_REFRESH_DELAY_MS)
  );
}

export async function refreshKeycloakSession(
  minValidity = REFRESH_WINDOW.seconds
) {
  const keycloak = getAppObj().keycloak;

  if (!sessionEstablished) {
    if (!keycloak?.authenticated || !keycloak.refreshToken) {
      return REFRESH_OUTCOME.NOT_DUE;
    }
    captureTokenTiming(keycloak);
  }

  if (isKeycloakSessionDead()) return REFRESH_OUTCOME.DEAD;

  let refreshed;

  try {
    refreshed = await keycloak.updateToken(minValidity);
  } catch (error) {
    return isKeycloakSessionDead() || isTokenEndpointClientError(error)
      ? REFRESH_OUTCOME.DEAD
      : REFRESH_OUTCOME.UNREACHABLE;
  }

  if (!refreshed) return REFRESH_OUTCOME.NOT_DUE;

  captureTokenTiming(keycloak);
  saveSsoTokens();
  scheduleRefresh();
  return REFRESH_OUTCOME.DONE;
}

async function refreshAndHandleDead(minValidity = REFRESH_WINDOW.seconds) {
  const outcome = await refreshKeycloakSession(minValidity);

  if (outcome === REFRESH_OUTCOME.DEAD) handleDeadSession();

  return outcome;
}

async function runScheduledRefresh() {
  const outcome = await refreshAndHandleDead();

  if (
    outcome === REFRESH_OUTCOME.NOT_DUE ||
    outcome === REFRESH_OUTCOME.UNREACHABLE
  ) {
    scheduleRetry();
  }
}

async function refreshAfterWakeUp(minValidity = REFRESH_WINDOW.seconds) {
  const outcome = await refreshAndHandleDead(minValidity);

  if (outcome === REFRESH_OUTCOME.UNREACHABLE) scheduleRetry();

  return outcome;
}

function handleVisibilityChange() {
  if (!document.hidden && sessionEstablished) refreshAfterWakeUp();
}

export function refreshKeycloakSessionBeforeRequest() {
  return refreshAndHandleDead();
}

export function attachKeycloakTokenExpiredHandler(keycloak) {
  keycloak.onTokenExpired = () => {
    console.log('[KC-DEBUG] keycloak.js/onTokenExpired: access token expired, refreshing'); // eslint-disable-line no-console
    refreshAfterWakeUp(FORCE_REFRESH_MIN_VALIDITY);
  };
}

export function startKeycloakSessionRefresh(keycloak) {
  captureTokenTiming(keycloak);
  sessionDeadHandled = false;

  if (typeof document !== 'undefined' && !visibilityListenerAttached) {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    visibilityListenerAttached = true;
  }

  scheduleRefresh();
}

export function stopKeycloakSessionRefresh() {
  clearScheduledRefresh();

  if (typeof document !== 'undefined' && visibilityListenerAttached) {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    visibilityListenerAttached = false;
  }

  tokenObtainedAt = null;
  tokenExpiresIn = null;
  refreshTokenExpiresIn = null;
  sessionEstablished = false;
  sessionDeadHandled = false;
}
